import { useQueryStates } from 'nuqs'
import { useMemo } from 'react'
import { useBrowsePageState } from '@/hooks/pages/useBrowsePageState'
import {
  buildParsers,
  countActiveFilters,
  fromUrl,
  resetPayload,
  toUrl,
  type AnyFilterSchema,
} from '@/lib/filterSchema'

/**
 * Schema-driven filter state for a browse page. Given a filter schema (the
 * single source of truth — see `@/lib/filterSchema`), this wires up the nuqs
 * URL state, the debounced search input + sort handlers (`useBrowsePageState`),
 * and derives the domain `filters` object, the change/reset handlers, and the
 * active-filter count — replacing the per-field hand-plumbing that previously
 * recurred across CardsPage / SealedProductsPage / the list pages.
 *
 * `TFilters` is the page's domain filter type (e.g. `CardFilters`); the schema's
 * keys are compile-time-constrained to it via `defineFilterSchema`.
 */
export function useSchemaFilterState<S extends AnyFilterSchema, TFilters>(schema: S) {
  // Parser map is derived once from the (module-constant) schema.
  const parsers = useMemo(() => buildParsers(schema), [schema])
  const [urlFilters, rawSetUrlFilters] = useQueryStates(parsers)

  // nuqs types its setter via a deeply-conditional mapped type that TS can't
  // unify with the schema-derived `SetPayloadFromSchema` while the schema is
  // still generic. Every payload we send (`toUrl`/`resetPayload` outputs, and
  // the `q`/`sort`/`order` updates from useBrowsePageState) is a valid subset of
  // the same runtime shape (field value | null), so narrow the setter once here
  // to a plain partial-update fn. This is the only assertion in the chain;
  // pages stay fully typed via `TFilters` and the schema's key set.
  const setUrlFilters = rawSetUrlFilters as unknown as (updates: Record<string, unknown>) => void

  const browseState = useBrowsePageState({ urlFilters, setUrlFilters })
  const { setSearchInput } = browseState

  const filters = fromUrl<S, TFilters>(schema, urlFilters as Record<keyof S & string, unknown>)

  const handleFilterChange = (newFilters: TFilters) => {
    setUrlFilters(toUrl(schema, newFilters as Record<string, unknown>) as Record<string, unknown>)
  }

  const handleResetFilters = () => {
    setUrlFilters(resetPayload(schema) as Record<string, unknown>)
    setSearchInput('')
  }

  const activeFilterCount = countActiveFilters(schema, filters as Record<string, unknown>)

  return {
    ...browseState,
    urlFilters,
    setUrlFilters,
    filters,
    handleFilterChange,
    handleResetFilters,
    activeFilterCount,
  }
}
